import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Sparkles, Disc } from 'lucide-react';
import { PRESET_PALETTES } from '../../utils/colors';

interface NewWheelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateWheel: (name: string, entries: string[], colorThemeId: string) => void;
}

interface TemplatePreset {
  name: string;
  theme: string;
  entries: string[];
}

const TEMPLATES: TemplatePreset[] = [
  {
    name: '🎯 Team Standup Order',
    theme: 'vibrant',
    entries: ['Alex', 'Sarah', 'Michael', 'Elena', 'David', 'Maya'],
  },
  {
    name: '⚖️ Yes / No / Maybe',
    theme: 'cyberpunk',
    entries: ['YES! ✅', 'NO ❌', 'MAYBE 🤔', 'ASK AGAIN 🔮'],
  },
  {
    name: '🎲 6-Sided Dice Roll',
    theme: 'arcade',
    entries: ['Number 1 ⚀', 'Number 2 ⚁', 'Number 3 ⚂', 'Number 4 ⚃', 'Number 5 ⚄', 'Number 6 ⚅'],
  },
  {
    name: '🍽️ Food & Dining',
    theme: 'sunset',
    entries: ['Pizza 🍕', 'Burgers 🍔', 'Sushi 🍣', 'Tacos 🌮', 'Curry 🍲', 'Salad 🥗'],
  },
  {
    name: '📅 Days of the Week',
    theme: 'pastel',
    entries: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  },
];

export const NewWheelModal: React.FC<NewWheelModalProps> = ({
  isOpen,
  onClose,
  onCreateWheel,
}) => {
  const [wheelName, setWheelName] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('vibrant');
  const [entriesText, setEntriesText] = useState('Option 1\nOption 2\nOption 3\nOption 4');

  const applyTemplate = (tmpl: TemplatePreset) => {
    setWheelName(tmpl.name);
    setSelectedTheme(tmpl.theme);
    setEntriesText(tmpl.entries.join('\n'));
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!wheelName.trim()) return;

    const entries = entriesText
      .split(/[\r\n]+/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    onCreateWheel(
      wheelName.trim(),
      entries.length > 0 ? entries : ['Option 1', 'Option 2'],
      selectedTheme
    );

    setWheelName('');
    setEntriesText('Option 1\nOption 2\nOption 3\nOption 4');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            padding: '1rem',
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="glass-panel"
            style={{
              width: '100%',
              maxWidth: '540px',
              padding: 'clamp(1rem, 4vw, 1.75rem)',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '20px',
              maxHeight: 'min(90vh, 90dvh)',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.25rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Disc size={22} color="var(--accent-primary)" />
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Create New Wheel</h3>
              </div>
              <button onClick={onClose} className="btn-ghost" style={{ padding: '6px' }}>
                <X size={18} />
              </button>
            </div>

            {/* Quick Templates */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Sparkles size={13} />
                Quick Templates
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {TEMPLATES.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    onClick={() => applyTemplate(t)}
                    style={{
                      fontSize: '0.75rem',
                      padding: '4px 10px',
                      borderRadius: '8px',
                      background: 'var(--bg-surface-elevated)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-secondary)',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--accent-primary)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-subtle)';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleCreate}>
              {/* Wheel Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                  Wheel Title
                </label>
                <input
                  type="text"
                  value={wheelName}
                  onChange={(e) => setWheelName(e.target.value)}
                  placeholder="e.g. Friday Team Giveaway"
                  required
                  autoFocus
                />
              </div>

              {/* Color Theme */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                  Color Theme
                </label>
                <select
                  value={selectedTheme}
                  onChange={(e) => setSelectedTheme(e.target.value)}
                >
                  {PRESET_PALETTES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Entries */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                  Initial Names / Items (one per line)
                </label>
                <textarea
                  value={entriesText}
                  onChange={(e) => setEntriesText(e.target.value)}
                  rows={5}
                  placeholder={'Option 1\nOption 2\nOption 3'}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button type="button" onClick={onClose} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!wheelName.trim()}
                  className="btn-primary"
                >
                  <Plus size={16} />
                  Create Wheel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
